import type { ToolClassification } from '../../types/message';
import {
  ASK_USER_CLOSE_TAG,
  ASK_USER_OPEN_TAG,
  parseAskUserCard,
  type AskUserCardData,
} from '../../helpers/askUser';

export type ResearchActivityKind = 'thinking' | 'reasoning' | 'narration' | 'question' | ToolClassification;

export interface ResearchActivityItem {
  kind: ResearchActivityKind;
  title?: string;
  content?: string;
  queries: string[];
  urls: string[];
  files: string[];
  isComplete: boolean;
  /** A tool that ran and failed. */
  isFailed?: boolean;
  /** For `question`: Buddy asking the user mid-answer, a step in the activity. */
  question?: AskUserCardData;
}

export interface ResearchActivityRun {
  items: ResearchActivityItem[];
  end: number;
  isComplete: boolean;
}

export interface ResearchActivityParseOptions {
  includeTrailingNarration?: boolean;
  codeRanges?: ReadonlyArray<{ start: number; end: number }>;
}

export interface ReasoningSummarySection {
  header?: string;
  content: string;
}

const RESEARCH_OPENERS = [
  '<think>',
  '<reasoning_summary>',
  '<tool_action>',
  '<tool_call>',
  ASK_USER_OPEN_TAG,
] as const;

const ACTIVITY_BLOCKERS = [
  '<resource>',
  '<artifacts>',
  '<image_generation>',
  '<shopping>',
  '<compose>',
  '<mail>',
] as const;

const uniqueStrings = (values: string[]): string[] => (
  Array.from(new Set(values.map(value => value.trim()).filter(Boolean)))
);

const parseStringArray = (value: string | null): string[] => {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed)
      ? uniqueStrings(parsed.filter((item): item is string => typeof item === 'string'))
      : [];
  } catch {
    return [];
  }
};

const extractLeaf = (body: string, tagName: string): string | null => {
  const opener = `<${tagName}>`;
  const closer = `</${tagName}>`;
  const start = body.indexOf(opener);
  if (start === -1) return null;

  const valueStart = start + opener.length;
  const end = body.indexOf(closer, valueStart);
  if (end !== -1) return body.slice(valueStart, end).trim();

  const nextTag = body.indexOf('<', valueStart);
  return body.slice(valueStart, nextTag === -1 ? body.length : nextTag).trim();
};

const parseToolItem = (body: string, isClosed: boolean): ResearchActivityItem => {
  const rawType = (extractLeaf(body, 'type') || '').toLowerCase();
  const validTypes: ToolClassification[] = ['vision', 'web', 'search', 'space', 'quiz', 'unknown'];
  const kind = validTypes.includes(rawType as ToolClassification)
    ? rawType as ToolClassification
    : 'unknown';
  // Live blocks carry a status; blocks saved before statuses existed are done.
  const status = extractLeaf(body, 'status') || 'done';

  return {
    kind,
    // Search queries ride in `success` for older clients.
    queries: uniqueStrings([
      ...parseStringArray(extractLeaf(body, 'queries')),
      ...parseStringArray(extractLeaf(body, 'success')),
    ]),
    urls: parseStringArray(extractLeaf(body, 'urls')),
    files: parseStringArray(extractLeaf(body, 'files')),
    isComplete: isClosed && status !== 'running',
    isFailed: status === 'error',
  };
};

const TITLE_CASE_MINOR_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in', 'into',
  'nor', 'of', 'on', 'or', 'over', 'per', 'the', 'to', 'via', 'vs', 'with',
]);

export const toTitleCase = (value: string): string => {
  const words = value.trim().split(/\s+/);
  return words
    .map((word, index) => {
      // Preserve acronyms, identifiers, and words with intentional casing
      if (/[A-Z]/.test(word.slice(1)) || /[\d_./]/.test(word)) return word;
      const lower = word.toLowerCase();
      const isEdge = index === 0 || index === words.length - 1;
      if (!isEdge && TITLE_CASE_MINOR_WORDS.has(lower)) return lower;
      return lower.replace(/^([^\p{L}]*)(\p{L})/u, (_, prefix, letter) => prefix + letter.toUpperCase());
    })
    .join(' ');
};

export const parseReasoningSummarySections = (
  content: string
): ReasoningSummarySection[] => {
  const sections: ReasoningSummarySection[] = [];
  let currentSection: ReasoningSummarySection | null = null;

  for (const line of content.split('\n')) {
    const boldMatch = line.match(/^\*\*(.*?)\*\*/);
    if (boldMatch) {
      if (currentSection && (currentSection.header || currentSection.content)) {
        sections.push(currentSection);
      }
      currentSection = {
        header: boldMatch[1].trim(),
        content: line.replace(/^\*\*(.*?)\*\*/, '').trim(),
      };
      continue;
    }

    if (!line.trim()) continue;
    if (!currentSection) currentSection = { content: '' };
    currentSection.content += `${currentSection.content ? '\n' : ''}${line}`;
  }

  if (currentSection && (currentSection.header || currentSection.content)) {
    sections.push(currentSection);
  }

  return sections;
};

const parseReasoningItems = (
  body: string,
  isComplete: boolean
): ResearchActivityItem[] => {
  const sections = parseReasoningSummarySections(body);
  const normalizedSections = sections.length > 0
    ? sections
    : [{ content: body.trim() }];

  return normalizedSections
    .filter(section => section.header || section.content)
    .map((section, index) => ({
      kind: 'reasoning',
      title: section.header ? toTitleCase(section.header) : undefined,
      content: section.content,
      queries: [],
      urls: [],
      files: [],
      isComplete: isComplete || index < normalizedSections.length - 1,
    }));
};

const parseItemAt = (
  text: string,
  start: number
): { items: ResearchActivityItem[]; end: number; isClosed: boolean } | null => {
  const opener = RESEARCH_OPENERS.find(candidate => text.startsWith(candidate, start));
  if (!opener) return null;

  if (opener === ASK_USER_OPEN_TAG) {
    // Cards arrive whole; a partial one renders nothing and ends the run.
    const openEnd = text.indexOf('>', start);
    const closeStart = openEnd === -1 ? -1 : text.indexOf(ASK_USER_CLOSE_TAG, openEnd);
    if (closeStart === -1) return { items: [], end: text.length, isClosed: false };
    const card = parseAskUserCard(text.slice(start, openEnd), text.slice(openEnd + 1, closeStart));
    return {
      items: card ? [{
        kind: 'question',
        queries: [],
        urls: [],
        files: [],
        isComplete: card.status !== 'pending',
        question: card,
      }] : [],
      end: closeStart + ASK_USER_CLOSE_TAG.length,
      isClosed: true,
    };
  }

  const tagName = opener.slice(1, -1);
  const closer = `</${tagName}>`;
  const contentStart = start + opener.length;
  const closeIndex = text.indexOf(closer, contentStart);
  const isComplete = closeIndex !== -1;
  const end = isComplete ? closeIndex + closer.length : text.length;
  const body = text.slice(contentStart, isComplete ? closeIndex : text.length);

  if (tagName === 'reasoning_summary') {
    return {
      items: parseReasoningItems(body, isComplete),
      end,
      isClosed: isComplete,
    };
  }

  if (tagName === 'think') {
    return {
      items: [{
        kind: 'thinking',
        content: body.trim(),
        queries: [],
        urls: [],
        files: [],
        isComplete,
      }],
      end,
      isClosed: isComplete,
    };
  }

  return { items: [parseToolItem(body, isComplete)], end, isClosed: isComplete };
};

const isInsideRange = (
  position: number,
  ranges: ReadonlyArray<{ start: number; end: number }>
): boolean => {
  for (const range of ranges) {
    if (position >= range.start && position < range.end) return true;
  }
  return false;
};

const findNextTag = (
  text: string,
  openers: readonly string[],
  from: number,
  codeRanges: ReadonlyArray<{ start: number; end: number }>
): { index: number; opener: string } | null => {
  let next: { index: number; opener: string } | null = null;

  for (const opener of openers) {
    let index = text.indexOf(opener, from);
    while (index !== -1) {
      const isInsideCode = isInsideRange(index, codeRanges);
      const previousCharacter = index > 0 ? text[index - 1] : null;
      const nextCharacter = index + opener.length < text.length
        ? text[index + opener.length]
        : null;
      const isQuoted = previousCharacter === '"' || previousCharacter === '\''
        || nextCharacter === '"' || nextCharacter === '\'';
      if (!isInsideCode && !isQuoted) break;
      index = text.indexOf(opener, index + opener.length);
    }

    if (index !== -1 && (!next || index < next.index)) {
      next = { index, opener };
    }
  }

  return next;
};

const narrationItem = (content: string): ResearchActivityItem => ({
  kind: 'narration',
  content,
  queries: [],
  urls: [],
  files: [],
  isComplete: true,
});

const cleanActivityStatusText = (content: string): string => (
  content
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .at(-1)
    ?.replace(/^#{1,6}\s+/, '')
    .replace(/\*\*|__|`/g, '')
    .replace(/\s+/g, ' ')
    .trim() || ''
);

const hostOf = (urlString: string): string | null => {
  try {
    return new URL(urlString).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
};

const runningToolLabel = (item: ResearchActivityItem): string => {
  switch (item.kind) {
    case 'search': {
      const [query] = item.queries;
      if (!query) return 'Searching the Web';
      const more = item.queries.length > 1 ? ` +${item.queries.length - 1}` : '';
      return `Searching \u201C${query}\u201D${more}`;
    }
    case 'web': {
      const host = item.urls.length === 1 ? hostOf(item.urls[0]) : null;
      if (host) return `Reading ${host}`;
      return item.urls.length > 0 ? `Reading ${item.urls.length} Pages` : 'Browsing the Web';
    }
    case 'vision':
      if (item.files.length === 1) return `Reading ${item.files[0]}`;
      if (item.files.length > 1) return `Reading ${item.files.length} Documents`;
      return item.urls.length > 0 ? 'Looking at the Image' : 'Reading Attachments';
    case 'space':
      return 'Reading Space Chats';
    case 'quiz':
      return item.queries[0] ? `Making a Quiz on ${item.queries[0]}` : 'Making a Quiz';
    default:
      return 'Working';
  }
};

/** The model reading what its tools brought back. */
const readingLabel = (urls: string[]): string | null => {
  if (urls.length === 0) return null;
  if (urls.length === 1) return `Reading ${hostOf(urls[0]) ?? '1 Source'}`;
  return `Reading ${urls.length} Sources`;
};

/** The question at the end of a run that is still waiting on the user. */
export const pendingQuestion = (items: ResearchActivityItem[]): AskUserCardData | null => {
  const last = items.at(-1);
  return last?.kind === 'question' && last.question?.status === 'pending' ? last.question : null;
};

export const askedLabel = (count: number, prefix: string): string => (
  count === 1 ? `${prefix} a Question` : `${prefix} ${count} Questions`
);

const isThoughtKind = (kind: ResearchActivityKind) => (
  kind === 'thinking' || kind === 'reasoning' || kind === 'narration'
);

/**
 * What a live research run is doing right now, read from its latest item:
 * a thought's last line, or what a tool is working on. `isThought` marks
 * streamed thought text, which is shown dimmed.
 */
export const getLiveResearchLabel = (
  items: ResearchActivityItem[]
): { text: string; isThought: boolean } | null => {
  const latestItem = items.at(-1);
  if (!latestItem) return null;
  if (isThoughtKind(latestItem.kind)) {
    const text = getActiveResearchStatus(items);
    return text ? { text, isThought: true } : null;
  }
  if (latestItem.kind === 'question') {
    const card = latestItem.question;
    return card?.status === 'pending'
      ? { text: askedLabel(card.questions.length, 'Buddy Asked You'), isThought: false }
      : null;
  }
  const text = latestItem.isComplete
    ? readingLabel(uniqueStrings(items.flatMap(item => item.urls)))
    : runningToolLabel(latestItem);
  return text ? { text, isThought: false } : null;
};

export const getActiveResearchStatus = (
  items: ResearchActivityItem[]
): string | null => {
  const latestItem = items.at(-1);
  if (!latestItem || !isThoughtKind(latestItem.kind)) {
    return null;
  }

  const title = latestItem.title?.trim();
  if (title) return title;

  const content = latestItem.content
    ? cleanActivityStatusText(latestItem.content)
    : '';
  return content || null;
};

export const parseResearchActivityRun = (
  text: string,
  start: number,
  options: ResearchActivityParseOptions = {}
): ResearchActivityRun | null => {
  const items: ResearchActivityItem[] = [];
  let cursor = start;
  let isComplete = true;
  const codeRanges = options.codeRanges || (() => {
    const ranges: Array<{ start: number; end: number }> = [];
    const codePattern = /```[\s\S]*?(?:```|$)|~~~[\s\S]*?(?:~~~|$)|`[^`\n]*(?:`|$)/g;
    let codeMatch: RegExpExecArray | null;
    while ((codeMatch = codePattern.exec(text)) !== null) {
      ranges.push({
        start: codeMatch.index,
        end: codeMatch.index + codeMatch[0].length,
      });
    }
    return ranges;
  })();

  while (cursor < text.length) {
    const parsed = parseItemAt(text, cursor);
    if (!parsed) break;

    items.push(...parsed.items);
    cursor = parsed.end;
    // A running tool block is whole markup, so the run continues past it;
    // only a tag still streaming in ends the run here.
    isComplete = isComplete && parsed.isClosed;

    if (!parsed.isClosed) break;

    const nextResearchTag = findNextTag(text, RESEARCH_OPENERS, cursor, codeRanges);
    const nextBlocker = findNextTag(text, ACTIVITY_BLOCKERS, cursor, codeRanges);
    const canContinueResearch = nextResearchTag
      && (!nextBlocker || nextResearchTag.index < nextBlocker.index);

    if (!canContinueResearch) {
      if (options.includeTrailingNarration) {
        const trailingEnd = nextBlocker?.index ?? text.length;
        const trailingText = text.slice(cursor, trailingEnd).trim();
        if (trailingText) items.push(narrationItem(trailingText));
        cursor = trailingEnd;
      }
      if (text.slice(cursor).trim().length === 0) cursor = text.length;
      break;
    }

    const intermediateText = text.slice(cursor, nextResearchTag.index).trim();
    if (intermediateText) items.push(narrationItem(intermediateText));
    cursor = nextResearchTag.index;
  }

  return items.length > 0 ? { items, end: cursor, isComplete } : null;
};
